import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Loader2, Bell, X, AlertTriangle, Settings, Send, RefreshCw, Copy, Star, Link2, Cloud, ShipWheel, Plus, Search, Info, Network, Unplug } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { useAuth } from '../hooks/useAuth';
import { isAdminOrOps } from '../utils/roles';
import { useCloudResourceFilter } from '../hooks/useCloudResourceFilter';
import { CategoryFilterDropdown } from '../components/CategoryFilterDropdown';
import { ReactComponent as PagerDutyIcon } from '../assets/icons/pagerduty-icon.svg';
import { apiClient, PagerDutyStatus, PagerDutyConfig, PagerDutyService, PagerDutyNamespaceAssignment, PagerDutyCloudResourceAssignment, InventoryCluster, InventoryNamespace, CloudResource } from '../api/client';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

interface ToastMessage {
  type: 'success' | 'error';
  message: string;
}

const PagerDutyIntegration: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState<PagerDutyStatus | null>(null);
  const [config, setConfig] = useState<PagerDutyConfig | null>(null);
  const [services, setServices] = useState<PagerDutyService[]>([]);
  const [namespaceAssignments, setNamespaceAssignments] = useState<PagerDutyNamespaceAssignment[]>([]);
  const [cloudResourceAssignments, setCloudResourceAssignments] = useState<PagerDutyCloudResourceAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Connection form state - Enhanced flow
  const [apiToken, setApiToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  // Connection form state - Legacy flow
  const [routingKey, setRoutingKey] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [useEnhancedFlow, setUseEnhancedFlow] = useState(true);

  // Service sync state
  const [syncing, setSyncing] = useState(false);
  const [configuringService, setConfiguringService] = useState<string | null>(null);

  // Namespace assignment dialog state
  const [namespaceDialogOpen, setNamespaceDialogOpen] = useState(false);
  const [clusters, setClusters] = useState<InventoryCluster[]>([]);
  const [namespaces, setNamespaces] = useState<InventoryNamespace[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<InventoryCluster | null>(null);
  const [selectedNamespace, setSelectedNamespace] = useState<InventoryNamespace | null>(null);
  const [selectedNsService, setSelectedNsService] = useState<string>('');
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [loadingNamespaces, setLoadingNamespaces] = useState(false);
  const [creatingNsAssignment, setCreatingNsAssignment] = useState(false);
  const [namespaceSearchTerm, setNamespaceSearchTerm] = useState('');
  const [debouncedNamespaceSearch, setDebouncedNamespaceSearch] = useState('');
  const [namespacePage, setNamespacePage] = useState(1);
  const [namespaceTotalPages, setNamespaceTotalPages] = useState(1);
  const namespacesPerPage = 20;

  // Cloud resource assignment dialog state
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const cloudFilter = useCloudResourceFilter({
    pageSize: 20,
    enableVPCs: true,
    enabled: resourceDialogOpen,
  });
  const [selectedResource, setSelectedResource] = useState<CloudResource | null>(null);
  const [selectedResourceService, setSelectedResourceService] = useState<string>('');
  const [creatingResourceAssignment, setCreatingResourceAssignment] = useState(false);

  // Configuration state
  const [editingConfig, setEditingConfig] = useState(false);
  const [configForm, setConfigForm] = useState<PagerDutyConfig>({
    notify_k8s_incidents: true,
    notify_cloud_incidents: true,
    skip_transient_incidents: true,
    min_severity: 'low',
    severity_mapping: {
      critical: 'critical',
      high: 'error',
      medium: 'warning',
      low: 'info',
    },
    cluster_overrides: {},
    incident_type_filters: [],
  });

  useEffect(() => {
    if (!authLoading && user) {
      fetchStatus();
    }
  }, [authLoading, user]);

  // Debounce namespace search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNamespaceSearch(namespaceSearchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [namespaceSearchTerm]);

  // Fetch namespaces when debounced search changes
  useEffect(() => {
    if (namespaceDialogOpen && selectedCluster) {
      setNamespacePage(1);
      fetchNamespacesPaginated(selectedCluster.uid, 1, debouncedNamespaceSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedNamespaceSearch]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getPagerDutyStatus();
      setStatus(data);

      if (data.connected) {
        const configData = await apiClient.getPagerDutyConfig();
        setConfig(configData);
        setConfigForm(configData);

        // Fetch services if API token is configured
        if (data.has_api_token) {
          try {
            const servicesData = await apiClient.getPagerDutyServices();
            setServices(servicesData);
          } catch (e) {
            console.error('Failed to fetch services:', e);
          }

          try {
            const nsAssignments = await apiClient.getPagerDutyNamespaceAssignments();
            setNamespaceAssignments(nsAssignments);
          } catch (e) {
            console.error('Failed to fetch namespace assignments:', e);
          }

          try {
            const crAssignments = await apiClient.getPagerDutyCloudResourceAssignments();
            setCloudResourceAssignments(crAssignments);
          } catch (e) {
            console.error('Failed to fetch cloud resource assignments:', e);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch PagerDuty status:', error);
      setToast({ type: 'error', message: 'Failed to load PagerDuty integration status' });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (useEnhancedFlow) {
      if (!apiToken.trim()) {
        setToast({ type: 'error', message: 'Please enter your PagerDuty API token' });
        return;
      }
      if (!webhookSecret.trim()) {
        setToast({ type: 'error', message: 'Please enter your PagerDuty webhook secret for secure webhook verification' });
        return;
      }
    } else {
      if (!routingKey.trim() || !serviceName.trim()) {
        setToast({ type: 'error', message: 'Please enter both routing key and service name' });
        return;
      }
    }

    try {
      setConnecting(true);
      if (useEnhancedFlow) {
        await apiClient.connectPagerDuty({
          api_token: apiToken.trim(),
          webhook_secret: webhookSecret.trim(),
        });
        setToast({ type: 'success', message: 'Connected to PagerDuty! Click "Sync Services" to import your services.' });
        setApiToken('');
        setWebhookSecret('');
      } else {
        await apiClient.connectPagerDuty({
          routing_key: routingKey.trim(),
          service_name: serviceName.trim(),
        });
        setToast({ type: 'success', message: `Connected to PagerDuty service: ${serviceName}` });
        setRoutingKey('');
        setServiceName('');
      }
      await fetchStatus();
    } catch (error: any) {
      console.error('Failed to connect PagerDuty:', error);
      const serverMsg =
        typeof error?.response?.data === 'string'
          ? error.response.data
          : error?.response?.data?.message || error?.response?.data?.error;
      setToast({
        type: 'error',
        message: serverMsg?.trim() || 'Failed to connect PagerDuty. Please check your credentials.',
      });
    } finally {
      setConnecting(false);
    }
  };

  const confirmDisconnect = async () => {
    try {
      setDisconnecting(true);
      await apiClient.disconnectPagerDuty();
      setStatus({ connected: false, has_api_token: false });
      setConfig(null);
      setServices([]);
      setNamespaceAssignments([]);
      setCloudResourceAssignments([]);
      setToast({ type: 'success', message: 'PagerDuty integration disconnected' });
      setDisconnectDialogOpen(false);
    } catch (error) {
      console.error('Failed to disconnect PagerDuty:', error);
      setToast({ type: 'error', message: 'Failed to disconnect PagerDuty' });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      await apiClient.testPagerDuty();
      setTestDialogOpen(false);
      setToast({ type: 'success', message: 'Test event sent to PagerDuty successfully!' });
    } catch (error) {
      console.error('Failed to test PagerDuty:', error);
      setTestDialogOpen(false);
      setToast({ type: 'error', message: 'Failed to send test event to PagerDuty' });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      await apiClient.updatePagerDutyConfig(configForm);
      setConfig(configForm);
      setEditingConfig(false);
      setToast({ type: 'success', message: 'Configuration saved successfully' });
    } catch (error) {
      console.error('Failed to save PagerDuty config:', error);
      setToast({ type: 'error', message: 'Failed to save configuration' });
    } finally {
      setSaving(false);
    }
  };

  const handleSyncServices = async () => {
    try {
      setSyncing(true);
      const result = await apiClient.syncPagerDutyServices();
      setToast({ type: 'success', message: `Synced ${result.synced_count} services from PagerDuty` });
      const servicesData = await apiClient.getPagerDutyServices();
      setServices(servicesData);
      await fetchStatus(); // Update last synced timestamp
    } catch (error) {
      console.error('Failed to sync services:', error);
      setToast({ type: 'error', message: 'Failed to sync services from PagerDuty' });
    } finally {
      setSyncing(false);
    }
  };

  const handleConfigureService = async (serviceId: string) => {
    try {
      setConfiguringService(serviceId);
      await apiClient.configurePagerDutyService(serviceId);
      const servicesData = await apiClient.getPagerDutyServices();

      // Auto-set as default if no other service is already default
      const hasDefault = servicesData.some(s => s.is_default);
      if (!hasDefault) {
        try {
          await apiClient.updatePagerDutyService(serviceId, true);
          const updatedServices = await apiClient.getPagerDutyServices();
          setServices(updatedServices);
          setToast({ type: 'success', message: 'Service configured and set as default' });
        } catch {
          setServices(servicesData);
          setToast({ type: 'success', message: 'Service configured successfully' });
        }
      } else {
        setServices(servicesData);
        setToast({ type: 'success', message: 'Service configured successfully' });
      }
    } catch (error) {
      console.error('Failed to configure service:', error);
      setToast({ type: 'error', message: 'Failed to configure service' });
    } finally {
      setConfiguringService(null);
    }
  };

  const handleSetDefaultService = async (serviceId: string) => {
    try {
      await apiClient.updatePagerDutyService(serviceId, true);
      setToast({ type: 'success', message: 'Default service updated' });
      const servicesData = await apiClient.getPagerDutyServices();
      setServices(servicesData);
    } catch (error) {
      console.error('Failed to set default service:', error);
      setToast({ type: 'error', message: 'Failed to set default service' });
    }
  };

  const handleDeleteNamespaceAssignment = async (assignmentId: string) => {
    try {
      await apiClient.deletePagerDutyNamespaceAssignment(assignmentId);
      setNamespaceAssignments(prev => prev.filter(a => a.id !== assignmentId));
      setToast({ type: 'success', message: 'Namespace assignment removed' });
    } catch (error) {
      console.error('Failed to delete namespace assignment:', error);
      setToast({ type: 'error', message: 'Failed to remove namespace assignment' });
    }
  };

  const handleDeleteCloudResourceAssignment = async (assignmentId: string) => {
    try {
      await apiClient.deletePagerDutyCloudResourceAssignment(assignmentId);
      setCloudResourceAssignments(prev => prev.filter(a => a.id !== assignmentId));
      setToast({ type: 'success', message: 'Cloud resource assignment removed' });
    } catch (error) {
      console.error('Failed to delete cloud resource assignment:', error);
      setToast({ type: 'error', message: 'Failed to remove cloud resource assignment' });
    }
  };

  const fetchNamespacesPaginated = async (clusterUid: string, page: number = 1, search: string = '') => {
    setLoadingNamespaces(true);
    try {
      const response = await apiClient.getInventoryNamespacesPaginated({
        cluster_id: clusterUid,
        page,
        page_size: namespacesPerPage,
        search: search || undefined,
      });
      setNamespaces(response?.namespaces || []);
      setNamespaceTotalPages(response?.total_pages || 1);
      setNamespacePage(response?.page || page);
    } catch (error) {
      console.error('Failed to fetch namespaces:', error);
      setToast({ type: 'error', message: 'Failed to load namespaces' });
      setNamespaces([]);
    } finally {
      setLoadingNamespaces(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ type: 'success', message: 'Copied to clipboard' });
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      setToast({ type: 'error', message: 'Failed to copy to clipboard. Please copy manually.' });
    }
  };

  // Namespace assignment handlers
  const openNamespaceDialog = async () => {
    setNamespaceDialogOpen(true);
    setSelectedCluster(null);
    setSelectedNamespace(null);
    setSelectedNsService('');
    setNamespaces([]);
    setNamespaceSearchTerm('');
    setDebouncedNamespaceSearch('');
    setNamespacePage(1);

    // Fetch clusters
    setLoadingClusters(true);
    try {
      const response = await apiClient.getInventoryClusters();
      setClusters(response.clusters || []);
    } catch (error) {
      console.error('Failed to fetch clusters:', error);
      setToast({ type: 'error', message: 'Failed to load clusters' });
    } finally {
      setLoadingClusters(false);
    }
  };

  const handleClusterChange = async (clusterUid: string) => {
    const cluster = clusters.find(c => c.uid === clusterUid);
    setSelectedCluster(cluster || null);
    setSelectedNamespace(null);
    setNamespaces([]);
    setNamespaceSearchTerm('');
    setDebouncedNamespaceSearch('');
    setNamespacePage(1);

    if (!clusterUid) return;

    fetchNamespacesPaginated(clusterUid, 1, '');
  };

  const handleCreateNamespaceAssignment = async () => {
    if (!selectedNamespace || !selectedNsService || !selectedCluster) {
      setToast({ type: 'error', message: 'Please select a cluster, namespace, and service' });
      return;
    }

    setCreatingNsAssignment(true);
    try {
      await apiClient.createPagerDutyNamespaceAssignment({
        service_id: selectedNsService,
        namespace_id: selectedNamespace.id,
        namespace_name: selectedNamespace.name,
        cluster_id: selectedCluster.uid,
        cluster_name: selectedCluster.name,
      });
      setToast({ type: 'success', message: 'Namespace assignment created' });
      setNamespaceDialogOpen(false);

      // Refresh assignments
      const nsAssignments = await apiClient.getPagerDutyNamespaceAssignments();
      setNamespaceAssignments(nsAssignments);
    } catch (error) {
      console.error('Failed to create namespace assignment:', error);
      setToast({ type: 'error', message: 'Failed to create namespace assignment' });
    } finally {
      setCreatingNsAssignment(false);
    }
  };

  // Cloud resource assignment handlers
  const openResourceDialog = () => {
    setResourceDialogOpen(true);
    setSelectedResource(null);
    setSelectedResourceService('');
    cloudFilter.reset();
    cloudFilter.refetchVPCs();
  };

  const handleCreateResourceAssignment = async () => {
    if (!selectedResource || !selectedResourceService) {
      setToast({ type: 'error', message: 'Please select a resource and service' });
      return;
    }

    setCreatingResourceAssignment(true);
    try {
      await apiClient.createPagerDutyCloudResourceAssignment({
        service_id: selectedResourceService,
        cloud_resource_id: selectedResource.id,
        resource_name: selectedResource.name,
        resource_type: selectedResource.resource_type,
        resource_arn: selectedResource.arn,
        cloud_provider: selectedResource.cloud_provider,
        aws_account_id: selectedResource.aws_account_id,
        region: selectedResource.region,
      });
      setToast({ type: 'success', message: 'Cloud resource assignment created' });
      setResourceDialogOpen(false);

      // Refresh assignments
      const crAssignments = await apiClient.getPagerDutyCloudResourceAssignments();
      setCloudResourceAssignments(crAssignments);
    } catch (error) {
      console.error('Failed to create cloud resource assignment:', error);
      setToast({ type: 'error', message: 'Failed to create cloud resource assignment' });
    } finally {
      setCreatingResourceAssignment(false);
    }
  };

  // Get configured services (only services with routing keys can receive alerts)
  const configuredServices = services.filter(s => s.is_configured);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Check authorization
  if (!isAdminOrOps(user?.role)) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Access denied. Only organization administrators or platform administrators can configure PagerDuty integration.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast notification */}
      {toast && (
        <Alert className={toast.type === 'error' ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950' : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'}>
          <div className="flex items-center justify-between">
            <AlertDescription className={toast.type === 'error' ? 'text-red-900 dark:text-red-100' : 'text-green-900 dark:text-green-100'}>
              {toast.message}
            </AlertDescription>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setToast(null)}
              aria-label="Dismiss notification"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Alert>
      )}

      {status?.connected ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <PagerDutyIcon className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  PagerDuty
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Incident alerting, on-call routing, and PagerDuty incident automation in workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tabs for connected state */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="services" disabled={!status.has_api_token}>Services</TabsTrigger>
                  <TabsTrigger value="namespaces" disabled={!status.has_api_token}>Namespaces</TabsTrigger>
                  <TabsTrigger value="resources" disabled={!status.has_api_token}>Cloud Resources</TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4 mt-4">
                  {status.has_api_token && (
                    <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                      <span className="text-sm text-muted-foreground">
                        {services.length > 0
                          ? `${services.length} service(s) synced`
                          : 'No services synced yet'
                        }
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSyncServices}
                        disabled={syncing}
                      >
                        {syncing ? (
                          <>
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Syncing...
                          </>
                        ) : (
                          <>
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Sync Services
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Notification status + configure */}
                  <div className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                      <span className="flex items-center gap-1.5">
                        <Bell className="h-4 w-4 text-muted-foreground" />
                        K8s alerts
                        <Badge variant="outline" className={config?.notify_k8s_incidents ? "text-green-600 border-green-600" : "text-muted-foreground"}>
                          {config?.notify_k8s_incidents ? 'On' : 'Off'}
                        </Badge>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Cloud className="h-4 w-4 text-muted-foreground" />
                        Cloud alerts
                        <Badge variant="outline" className={config?.notify_cloud_incidents ? "text-green-600 border-green-600" : "text-muted-foreground"}>
                          {config?.notify_cloud_incidents ? 'On' : 'Off'}
                        </Badge>
                      </span>
                      <span className="text-muted-foreground">Min severity: {config?.min_severity || 'low'}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingConfig(!editingConfig)}
                    >
                      <Settings className="h-3 w-3 mr-1" />
                      {editingConfig ? 'Hide' : 'Configure'}
                    </Button>
                  </div>

                  {/* Configuration Panel */}
                  {editingConfig && (
                    <div className="mt-4 p-4 border rounded-lg space-y-5">
                      <h4 className="text-sm font-semibold">Notification Settings</h4>

                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Incident Types</Label>
                        <div className="space-y-3 pt-1">
                          <label htmlFor="notify_k8s" className="flex items-center gap-3 p-2.5 rounded-md border bg-card hover:bg-muted/50 transition-colors cursor-pointer">
                            <Checkbox
                              id="notify_k8s"
                              checked={configForm.notify_k8s_incidents}
                              onCheckedChange={(checked) =>
                                setConfigForm(prev => ({ ...prev, notify_k8s_incidents: checked === true }))
                              }
                            />
                            <div>
                              <span className="text-sm font-medium">Kubernetes incidents</span>
                              <p className="text-xs text-muted-foreground">CrashLoopBackOff, OOMKilled, image pull errors, etc.</p>
                            </div>
                          </label>

                          <label htmlFor="notify_cloud" className="flex items-center gap-3 p-2.5 rounded-md border bg-card hover:bg-muted/50 transition-colors cursor-pointer">
                            <Checkbox
                              id="notify_cloud"
                              checked={configForm.notify_cloud_incidents}
                              onCheckedChange={(checked) =>
                                setConfigForm(prev => ({ ...prev, notify_cloud_incidents: checked === true }))
                              }
                            />
                            <div>
                              <span className="text-sm font-medium">Cloud incidents (AWS/OCI)</span>
                              <p className="text-xs text-muted-foreground">IAM anomalies, security group changes, GuardDuty findings, etc.</p>
                            </div>
                          </label>

                          <label htmlFor="skip_transient" className="flex items-center gap-3 p-2.5 rounded-md border bg-card hover:bg-muted/50 transition-colors cursor-pointer">
                            <Checkbox
                              id="skip_transient"
                              checked={configForm.skip_transient_incidents}
                              onCheckedChange={(checked) =>
                                setConfigForm(prev => ({ ...prev, skip_transient_incidents: checked === true }))
                              }
                            />
                            <div>
                              <span className="text-sm font-medium">Skip transient incidents</span>
                              <p className="text-xs text-muted-foreground">Don't notify for auto-resolving issues like node scaling or CNI restarts</p>
                            </div>
                          </label>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5">
                          <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Minimum Severity</Label>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[220px]">
                                <p className="text-xs">Only incidents at or above this severity level will trigger PagerDuty alerts.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <Select
                          value={configForm.min_severity}
                          onValueChange={(value) => setConfigForm(prev => ({ ...prev, min_severity: value }))}
                        >
                          <SelectTrigger className="w-[200px]">
                            <SelectValue placeholder="Select severity" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low (all incidents)</SelectItem>
                            <SelectItem value="medium">Medium and above</SelectItem>
                            <SelectItem value="high">High and above</SelectItem>
                            <SelectItem value="critical">Critical only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button onClick={handleSaveConfig} disabled={saving}>
                          {saving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            'Save Changes'
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setConfigForm(config || configForm);
                            setEditingConfig(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Service Details */}
                  <div className="text-xs text-muted-foreground mt-4 pt-3 border-t flex flex-wrap gap-x-4 gap-y-1">
                    {status.service_name && <span>Service: {status.service_name}</span>}
                    {status.created_at && (
                      <span>Connected: {new Date(status.created_at * 1000).toLocaleDateString()}</span>
                    )}
                    {status.last_synced_at && (
                      <span>Synced: {new Date(status.last_synced_at * 1000).toLocaleString()}</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span tabIndex={services.length === 0 ? 0 : undefined}>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setTestDialogOpen(true)}
                              disabled={services.length === 0}
                            >
                              <Send className="h-3 w-3 mr-1" />
                              Test Connection
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {services.length === 0 && (
                          <TooltipContent side="bottom">
                            <p>Sync services before testing the connection</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDisconnectDialogOpen(true)}
                      className="gap-1"
                    >
                      <Unplug className="w-3.5 h-3.5" /> Disconnect
                    </Button>
                  </div>
                </TabsContent>

                {/* Services Tab */}
                <TabsContent value="services" className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">PagerDuty Services</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSyncServices}
                      disabled={syncing}
                    >
                      {syncing ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-3 w-3 mr-1" />
                          Sync Services
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Configure a service to start routing alerts to it.
                  </p>

                  {services.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      <p>No services synced. Click "Sync Services" to import them.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {services.map(service => (
                        <div key={service.id} className="flex items-center gap-3 rounded-lg border p-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{service.pagerduty_service_name}</span>
                              {service.is_default && (
                                <Badge variant="secondary" className="text-xs">
                                  <Star className="h-3 w-3 mr-1" />
                                  Default
                                </Badge>
                              )}
                            </div>
                            {service.description && (
                              <div className="text-xs text-muted-foreground mt-1">{service.description}</div>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={service.is_configured ? "text-green-600 border-green-600" : "text-muted-foreground"}>
                              {service.is_configured ? 'Configured' : 'Not Configured'}
                            </Badge>
                            {!service.is_configured && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleConfigureService(service.id)}
                                disabled={configuringService === service.id}
                              >
                                {configuringService === service.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  'Configure'
                                )}
                              </Button>
                            )}
                            {service.is_configured && !service.is_default && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSetDefaultService(service.id)}
                              >
                                Set Default
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Namespaces Tab */}
                <TabsContent value="namespaces" className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium">Namespace Routing</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Route Kubernetes incidents to specific services by namespace
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openNamespaceDialog}
                      disabled={configuredServices.length === 0}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Assignment
                    </Button>
                  </div>
                  {configuredServices.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      Configure a service in the Services tab first.
                    </p>
                  )}

                  {namespaceAssignments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      <ShipWheel className="h-6 w-6 mx-auto mb-2 opacity-50" />
                      <p>No assignments yet. Incidents route to the default service.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {namespaceAssignments.map(assignment => (
                        <div key={assignment.id} className="flex items-center gap-3 rounded-lg border p-3">
                          <ShipWheel className="h-5 w-5 text-muted-foreground" />
                          <div className="flex-1">
                            <div className="text-sm font-medium">{assignment.namespace_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {assignment.cluster_name || assignment.cluster_id}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Link2 className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{assignment.service_name}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteNamespaceAssignment(assignment.id)}
                            aria-label="Remove namespace assignment"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Cloud Resources Tab */}
                <TabsContent value="resources" className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium">Cloud Resource Routing</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Route cloud incidents to specific services by resource
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openResourceDialog}
                      disabled={configuredServices.length === 0}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Assignment
                    </Button>
                  </div>
                  {configuredServices.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      Configure a service in the Services tab first.
                    </p>
                  )}

                  {cloudResourceAssignments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      <Cloud className="h-6 w-6 mx-auto mb-2 opacity-50" />
                      <p>No assignments yet. Incidents route to the default service.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {cloudResourceAssignments.map(assignment => (
                        <div key={assignment.id} className="flex items-center gap-3 rounded-lg border p-3">
                          <Cloud className="h-5 w-5 text-muted-foreground" />
                          <div className="flex-1">
                            <div className="text-sm font-medium">{assignment.resource_name || assignment.resource_arn || assignment.cloud_resource_id}</div>
                            <div className="text-xs text-muted-foreground">
                              {assignment.resource_type} • {assignment.cloud_provider.toUpperCase()}
                              {assignment.region && ` • ${assignment.region}`}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Link2 className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{assignment.service_name}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCloudResourceAssignment(assignment.id)}
                            aria-label="Remove cloud resource assignment"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <PagerDutyIcon className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>Connect PagerDuty</CardTitle>
                <CardDescription>Connect PagerDuty to route incident alerts to on-call teams, trigger RCAs from PagerDuty incidents, and manage alerts via workflows.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
              {/* Connection Form */}
              <div className="space-y-4">
                {/* Flow Toggle */}
                <div className="inline-flex rounded-md border border-input bg-background p-0.5">
                  <button
                    type="button"
                    onClick={() => setUseEnhancedFlow(true)}
                    className={`px-3 py-1.5 text-sm rounded-[5px] transition-colors ${
                      useEnhancedFlow ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    API Token <span className="text-muted-foreground font-normal">(recommended)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseEnhancedFlow(false)}
                    className={`px-3 py-1.5 text-sm rounded-[5px] transition-colors ${
                      !useEnhancedFlow ? 'bg-accent text-accent-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Routing Key
                  </button>
                </div>

                {useEnhancedFlow ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="api_token">PagerDuty API Token</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className="text-muted-foreground hover:text-foreground transition-colors">
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80" side="right">
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium">Setup Instructions</h4>
                              <ol className="space-y-1.5 text-xs text-muted-foreground">
                                {[
                                  'In PagerDuty, click your user icon and go to "My Profile"',
                                  'Go to "User Settings" tab',
                                  'Click "Create API User Token" (this must be a user-level token, not an account-level API Access Key from Configuration → API Access)',
                                  'Copy the token and paste it below',
                                  'After connecting, click "Sync Services" to import your services',
                                ].map((step, i) => (
                                  <li key={i} className="flex gap-2">
                                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">{i + 1}</span>
                                    <span>{step}</span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <Input
                        id="api_token"
                        type="password"
                        placeholder="User-level API token (My Profile → User Settings → Create API User Token)"
                        value={apiToken}
                        onChange={(e) => setApiToken(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Must be a <strong>user-level</strong> token created from <em>My Profile → User Settings → Create API User Token</em>. Account-level API Access Keys (from Configuration → API Access) will not work — Kestrel uses the token owner's identity to attribute notes and escalations on your incidents.
                      </p>
                    </div>

                    {/* Show webhook URL for user to configure in PagerDuty */}
                    {status?.webhook_url && (
                      <div className="space-y-2 p-3 bg-muted rounded-lg">
                        <div className="flex items-center gap-1.5">
                          <Label className="text-sm font-medium">Webhook URL</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <button type="button" aria-label="Webhook setup instructions" className="text-muted-foreground hover:text-foreground transition-colors">
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80" side="right">
                              <div className="space-y-2">
                                <h4 className="text-sm font-medium">Webhook Setup Instructions</h4>
                                <ol className="space-y-1.5 text-xs text-muted-foreground">
                                  {[
                                    'In PagerDuty, go to Integrations > Generic Webhooks (V3)',
                                    'Click "New Webhook"',
                                    'Paste the Webhook URL below',
                                    'For Scope Type, select "Account" to receive all events for this integration',
                                    'Add the events you want to subscribe to',
                                    'Copy the signing secret and paste it below',
                                  ].map((step, i) => (
                                    <li key={i} className="flex gap-2">
                                      <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">{i + 1}</span>
                                      <span>{step}</span>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs bg-background p-2 rounded border overflow-x-auto">
                            {status.webhook_url}
                          </code>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(status.webhook_url!)}
                            aria-label="Copy webhook URL"
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="webhook_secret">Webhook Signing Secret <span className="text-destructive">*</span></Label>
                      <Input
                        id="webhook_secret"
                        type="password"
                        placeholder="Enter webhook signing secret from PagerDuty"
                        value={webhookSecret}
                        onChange={(e) => setWebhookSecret(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        From the webhook you created above
                      </p>
                    </div>

                    <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
                      Notes and escalations Kestrel performs on your incidents will be attributed to the PagerDuty user whose API token you provide above.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center gap-1.5">
                        <Label htmlFor="routing_key">Integration/Routing Key</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className="text-muted-foreground hover:text-foreground transition-colors">
                              <Info className="h-3.5 w-3.5" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80" side="right">
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium">Setup Instructions</h4>
                              <ol className="space-y-1.5 text-xs text-muted-foreground">
                                {[
                                  'In PagerDuty, go to Services and select (or create) a service',
                                  'Navigate to Integrations tab',
                                  'Add a new "Events API v2" integration',
                                  'Copy the Integration Key (routing key)',
                                  'Paste it above and click Connect',
                                ].map((step, i) => (
                                  <li key={i} className="flex gap-2">
                                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary/10 text-primary text-[10px] flex items-center justify-center font-medium">{i + 1}</span>
                                    <span>{step}</span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <Input
                        id="routing_key"
                        type="password"
                        placeholder="Enter your PagerDuty Events API v2 routing key"
                        value={routingKey}
                        onChange={(e) => setRoutingKey(e.target.value)}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service_name">Service Name</Label>
                      <Input
                        id="service_name"
                        placeholder="e.g., Kestrel Security Alerts"
                        value={serviceName}
                        onChange={(e) => setServiceName(e.target.value)}
                      />
                    </div>
                  </>
                )}

                <Button
                  onClick={handleConnect}
                  disabled={connecting || (useEnhancedFlow ? (!apiToken.trim() || !webhookSecret.trim()) : (!routingKey.trim() || !serviceName.trim()))}
                >
                  {connecting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    'Connect PagerDuty'
                  )}
                </Button>
              </div>
          </CardContent>
        </Card>
      )}

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Disconnect PagerDuty?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately disconnect the PagerDuty integration. You will no longer be able to:
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li>Trigger workflows from PagerDuty incidents</li>
                <li>Route incident alerts to on-call teams</li>
                <li>Manage PagerDuty alerts via workflow actions</li>
              </ul>
              <strong className="block mt-3">You can reconnect at any time.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisconnect}
              disabled={disconnecting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {disconnecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect PagerDuty'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Test Connection Confirmation Dialog */}
      <AlertDialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader className="space-y-3">
            <AlertDialogTitle>
              Test PagerDuty Connection
            </AlertDialogTitle>
            <AlertDialogDescription className="leading-relaxed">
              This will send a test event to your default PagerDuty service to verify the integration is working correctly. The test incident will be automatically resolved immediately, so it will not page your on-call team.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={testing}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleTestConnection}
              disabled={testing}
            >
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Test Incident'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Namespace Assignment Dialog */}
      <Dialog open={namespaceDialogOpen} onOpenChange={setNamespaceDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Namespace Assignment</DialogTitle>
            <DialogDescription>
              Route K8s incidents from a specific namespace to a PagerDuty service.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Cluster Selection */}
            <div className="space-y-2">
              <Label>Cluster</Label>
              <Select value={selectedCluster?.uid || ''} onValueChange={handleClusterChange} disabled={loadingClusters}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingClusters ? "Loading clusters..." : "Select a cluster"} />
                </SelectTrigger>
                <SelectContent>
                  {clusters.map(cluster => (
                    <SelectItem key={cluster.uid} value={cluster.uid}>
                      {cluster.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Namespace Search and Selection */}
            <div className="space-y-2">
              <Label>Namespace</Label>
              {!selectedCluster ? (
                <div className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-md">
                  Select a cluster first to browse namespaces
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search namespaces..."
                      value={namespaceSearchTerm}
                      onChange={(e) => setNamespaceSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-2 border rounded-md p-2">
                    {loadingNamespaces ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : namespaces.length > 0 ? (
                      namespaces.map((ns) => (
                        <div
                          key={ns.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedNamespace(ns)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedNamespace(ns);
                            }
                          }}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                            selectedNamespace?.id === ns.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-primary/50 hover:bg-muted/50'
                          }`}
                        >
                          <p className="font-medium text-sm">{ns.name}</p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-6">
                        <ShipWheel className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          {namespaceSearchTerm ? 'No namespaces found matching your search.' : 'No namespaces available.'}
                        </p>
                      </div>
                    )}
                  </div>
                  {/* Pagination controls */}
                  {namespaceTotalPages > 1 && !loadingNamespaces && (
                    <div className="flex items-center justify-between pt-2">
                      <p className="text-xs text-muted-foreground">
                        Page {namespacePage} of {namespaceTotalPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const newPage = namespacePage - 1;
                            setNamespacePage(newPage);
                            fetchNamespacesPaginated(selectedCluster.uid, newPage, debouncedNamespaceSearch);
                          }}
                          disabled={namespacePage <= 1}
                        >
                          Previous
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const newPage = namespacePage + 1;
                            setNamespacePage(newPage);
                            fetchNamespacesPaginated(selectedCluster.uid, newPage, debouncedNamespaceSearch);
                          }}
                          disabled={namespacePage >= namespaceTotalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* PagerDuty Service Selection */}
            <div className="space-y-2">
              <Label>PagerDuty Service</Label>
              <Select value={selectedNsService} onValueChange={setSelectedNsService}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a PagerDuty service" />
                </SelectTrigger>
                <SelectContent>
                  {configuredServices.map(service => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.pagerduty_service_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNamespaceDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateNamespaceAssignment}
              disabled={!selectedCluster || !selectedNamespace || !selectedNsService || creatingNsAssignment}
            >
              {creatingNsAssignment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Assignment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cloud Resource Assignment Dialog */}
      <Dialog open={resourceDialogOpen} onOpenChange={setResourceDialogOpen}>
        <DialogContent className="max-w-2xl flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Cloud Resource Assignment</DialogTitle>
            <DialogDescription>
              Route cloud incidents from a specific resource to a PagerDuty service.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-4 min-h-0 flex-1">
            {/* Cloud Resource Search and Selection */}
            <div className="space-y-2">
              <Label>Cloud Resource</Label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search cloud resources..."
                    value={cloudFilter.searchTerm}
                    onChange={(e) => cloudFilter.setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <CategoryFilterDropdown
                  variant="inline"
                  selectedCategories={cloudFilter.selectedCategories}
                  onCategoryChange={cloudFilter.handleCategoryChange}
                  onToggleAll={cloudFilter.toggleAllCategories}
                />
              </div>
              <div className="min-h-0 flex-1 max-h-64 overflow-y-auto space-y-2 border rounded-md p-2">
                {cloudFilter.loading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (cloudFilter.resources.length > 0 || (cloudFilter.showVpcGroups && cloudFilter.vpcs.length > 0)) ? (
                  <>
                    {/* VPC groups when Network category is selected */}
                    {cloudFilter.showVpcGroups && cloudFilter.vpcs.length > 0 && (
                      <>
                        <div className="px-1">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">VPC Groups</p>
                        </div>
                        {cloudFilter.vpcs.map(vpc => (
                          <div
                            key={vpc.vpc_id}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              const vpcResource = cloudFilter.resources.find(r => r.resource_id === vpc.vpc_id || r.vpc_id === vpc.vpc_id && (r.resource_type === 'AWS::EC2::VPC' || r.resource_type === 'OCI::Core::VCN'));
                              if (vpcResource) setSelectedResource(vpcResource);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                const vpcResource = cloudFilter.resources.find(r => r.resource_id === vpc.vpc_id || r.vpc_id === vpc.vpc_id && (r.resource_type === 'AWS::EC2::VPC' || r.resource_type === 'OCI::Core::VCN'));
                                if (vpcResource) setSelectedResource(vpcResource);
                              }
                            }}
                            className="p-3 border-2 border-dashed border-blue-500/30 hover:border-blue-500/60 hover:bg-blue-500/5 rounded-lg cursor-pointer transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Network className="h-4 w-4 text-muted-foreground" />
                                  <p className="font-medium text-sm">{vpc.vpc_name || vpc.vpc_id}</p>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {vpc.vpc_id} · {vpc.region} · {vpc.cloud_provider?.toUpperCase()}
                                </p>
                              </div>
                              <Badge variant="secondary" className="text-xs">
                                {vpc.resource_count} resources
                              </Badge>
                            </div>
                          </div>
                        ))}
                        <div className="px-1 pt-1">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Individual Resources</p>
                        </div>
                      </>
                    )}
                    {cloudFilter.resources.filter(r => !(cloudFilter.showVpcGroups && (r.resource_type === 'AWS::EC2::VPC' || r.resource_type === 'OCI::Core::VCN'))).map((resource) => (
                      <div
                        key={resource.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedResource(resource)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedResource(resource);
                          }
                        }}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                          selectedResource?.id === resource.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50 hover:bg-muted/50'
                        }`}
                      >
                        <p className="font-medium text-sm truncate">{resource.name || resource.resource_id || 'Unnamed Resource'}</p>
                        <p className="text-xs text-muted-foreground">
                          {resource.cloud_provider?.toUpperCase()} • {resource.resource_type}
                          {resource.region && ` • ${resource.region}`}
                        </p>
                        {resource.arn && (
                          <p className="text-xs text-muted-foreground truncate">{resource.arn}</p>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="text-center py-6">
                    <Cloud className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {cloudFilter.searchTerm ? 'No cloud resources found matching your search.' : 'No cloud resources available.'}
                    </p>
                  </div>
                )}
              </div>
              {/* Pagination controls */}
              {cloudFilter.totalPages > 1 && !cloudFilter.loading && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-muted-foreground">
                    Page {cloudFilter.page} of {cloudFilter.totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => cloudFilter.setPage(p => p - 1)}
                      disabled={cloudFilter.page <= 1}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => cloudFilter.setPage(p => p + 1)}
                      disabled={cloudFilter.page >= cloudFilter.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* PagerDuty Service Selection */}
            <div className="space-y-2">
              <Label>PagerDuty Service</Label>
              <Select value={selectedResourceService} onValueChange={setSelectedResourceService}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a PagerDuty service" />
                </SelectTrigger>
                <SelectContent>
                  {configuredServices.map(service => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.pagerduty_service_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResourceDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateResourceAssignment}
              disabled={!selectedResource || !selectedResourceService || creatingResourceAssignment}
            >
              {creatingResourceAssignment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Assignment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PagerDutyIntegration;
